import React, { useState, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "store/reducers";
import { updateMediaDevices } from "store/media-devices/thunks";
import {
  setAudioOutput,
  setLocalVideoTrackInfo,
  setLocalAudioTrackInfo
} from "store/local-tracks/slice";
import { EnterName } from "./EnterName";
import { RequestPermissions } from "./RequestPermissions";
import { SelectMediaDevices } from "components/shared/select-media-devices/SelectMediaDevices";
import { LobbyStep, NetworkQuality } from "store/user/types";
import styles from "./Lobby.module.css";
import { ErrorAlert } from "components/shared/error/ErrorAlert";
import { NetworkHelpDialog } from "components/shared/network-help-dialog/NetworkHelpDialog";
import {
  setErrorNotification,
  removeErrorNotification,
} from "store/errors/slice";
import { ErrorType } from "store/errors/types";
import { isSupported } from "twilio-video";
import { getBrowser, getOS, isWebView, twilioSyncEnabled } from "utils";
import { setLobbyStep, setNetworkQuality, setUsername } from "store/user/slice";
import { useIsMountedRef } from "hooks/useIsMountedRef";
import { AppDispatch } from "store/store";
import { useSyncClientLobby } from "components/room/useSyncClientLobby";
import {
  MediaConnectionBitrateTest,
  testMediaConnectionBitrate,
} from "@twilio/rtc-diagnostics";
import { fetchToken } from "store/user/thunks";
import { client } from "api/client";
import { AudioTestDialog } from "components/shared/audio-test-dialog/AudioTestDialog";
import { PayloadAction } from "@reduxjs/toolkit";
import rollbar from "helpers/rollbar";
import {
  MediaDevicesState,
} from "store/media-devices/types";
import SyncClient from "twilio-sync";
import { isMobile } from "react-device-detect";

interface LobbyProps {
  roomUid: string;
  fetchSyncClient: (roomCode: string) => void;
  syncClient: SyncClient | undefined;
}

const webViewError = {
  type: ErrorType.WebViewError,
  name: "WebView Error",
  message: `In-app browsers are not supported. Please open this web page in a recent version of ${
    getOS().name === "iOS" ? "Safari." : "Chrome or Firefox."
  }`,
};

export const Lobby: React.FC<LobbyProps> = ({
  roomUid,
  fetchSyncClient,
  syncClient,
}) => {
  const isMountedRef = useIsMountedRef();
  const { error } = useSelector((s: RootState) => s.errors);
  const dispatch = useDispatch<AppDispatch>();
  const [roomCode, setRoomCode] = useState(roomUid);
  const mediaDevices = useSelector((s: RootState) => s.mediaDevices);
  const localTracks = useSelector((s: RootState) => s.localTracks);
  const {
    username,
    lobbyStep,
    networkQuality,
    showNetworkQualityHelp,
    showAudioTest,
  } = useSelector((s: RootState) => s.user);
  const [hasGrantedPermissions, setHasGrantedPermissions] = useState(false);
  const { joinSyncRoom } = useSyncClientLobby(syncClient);

  const validateRoomCodeAndUserName = useCallback((): {
    hasError: boolean;
    errorMessage: string;
  } => {
    const guidRegex =
      /[0-9A-Fa-f]{8}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{4}[-][0-9A-Fa-f]{12}/;
    const roomCodeIsValidGuid = guidRegex.test(roomCode);

    if (!roomCodeIsValidGuid) {
      return {
        hasError: true,
        errorMessage: "Please enter a valid room code.",
      };
    }

    if (!username) {
      return { hasError: true, errorMessage: "Please enter a name." };
    }

    return {
      hasError: false,
      errorMessage: "",
    };
  }, [username, roomCode]);

  const startMediaConnectionBitrateTest = useCallback(async () => {
    const token = await client.twilio_GetNtsToken(roomCode);

    const iceServers =
      token.ice_servers?.map((is) => {
        return {
          credential: is.credential,
          urls: [is.url],
          username: is.username,
        } as RTCIceServer;
      }) || [];

    const mediaConnectionBitrateTest = testMediaConnectionBitrate({
      iceServers: iceServers,
    });

    mediaConnectionBitrateTest.on(
      MediaConnectionBitrateTest.Events.Error,
      (error) => {
        if (isMountedRef.current) {
          dispatch(setNetworkQuality(NetworkQuality.Error));
          dispatch(
            setErrorNotification({
              name: error.name,
              type: ErrorType.ConnectionError,
              message: error.message,
            })
          );
        }
      }
    );

    mediaConnectionBitrateTest.on(
      MediaConnectionBitrateTest.Events.End,
      (report) => {
        if (isMountedRef.current) {
          if (report.averageBitrate < 1000) {
            return dispatch(setNetworkQuality(NetworkQuality.Poor));
          } else if (report.averageBitrate < 2500) {
            return dispatch(setNetworkQuality(NetworkQuality.Fair));
          } else if (report.averageBitrate < 5000) {
            return dispatch(setNetworkQuality(NetworkQuality.Good));
          } else {
            return dispatch(setNetworkQuality(NetworkQuality.Excellent));
          }
        }
      }
    );

    setTimeout(() => {
      mediaConnectionBitrateTest.stop();
    }, 5000);
  }, [dispatch, isMountedRef, roomCode]);

  const submitRoomCodeAndUserName = useCallback(async () => {
    dispatch(removeErrorNotification());
    const { hasError, errorMessage } = validateRoomCodeAndUserName();

    if (hasError) {
      return dispatch(
        setErrorNotification({
          name: "Error",
          type: ErrorType.ConnectionError,
          message: errorMessage,
        })
      );
    }

    try {
      // Start the bit rate test if the room code is not
      // in the path, and was entered by the user.
      await startMediaConnectionBitrateTest();
      
      hasGrantedPermissions
        ? dispatch(setLobbyStep(LobbyStep.SelectMediaDevices))
        : dispatch(setLobbyStep(LobbyStep.RequestPermission));
      fetchSyncClient(roomCode);
    } catch (err) {
      dispatch(
        setErrorNotification({
          name: err.name,
          type: ErrorType.ConnectionError,
          message: err.message,
        })
      );
      rollbar.error(err);
    }
  }, [
    dispatch,
    roomCode,
    hasGrantedPermissions,
    fetchSyncClient,
    startMediaConnectionBitrateTest,
    validateRoomCodeAndUserName,
  ]);

  const requestPermissions = useCallback(async () => {
    try {
      let options: MediaStreamConstraints = {
        audio: true,
        video: {
          aspectRatio: 1.7777777778,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(options);
      const { payload: devices }: PayloadAction<any> = await dispatch(
        updateMediaDevices()
      );
      let videoTrack = devices.videoInputs[0];
      let audioTrack = devices.audioInputs[0];

      if (/Firefox/.test(getBrowser().name || "")) {
        const permittedVideoDeviceId = stream
          .getVideoTracks()[0]
          ?.getSettings()?.deviceId;
        const permittedAudioDeviceId = stream
          .getAudioTracks()[0]
          ?.getSettings()?.deviceId;
        videoTrack = devices.videoInputs.find(
          (v: any) => v.deviceId === permittedVideoDeviceId
        );
        audioTrack = devices.audioInputs.find(
          (a: any) => a.deviceId === permittedAudioDeviceId
        );
      }

      dispatch(
        setLocalVideoTrackInfo({
          deviceId: videoTrack?.deviceId || "",
          label: videoTrack?.label || "",
        })
      );

      dispatch(
        setLocalAudioTrackInfo({
          deviceId: audioTrack?.deviceId || "",
          label: audioTrack?.label,
          isDefault: audioTrack?.isDefault
        })
      );

      dispatch(setLobbyStep(LobbyStep.SelectMediaDevices));
      dispatch(removeErrorNotification());
    } catch (err) {
      let error = {
        name: err.name,
        type: ErrorType.MediaError,
        message: err.message,
      };

      if (isWebView()) {
        error = webViewError;
      }

      dispatch(setErrorNotification(error));

      rollbar.error(err);
    }
  }, [dispatch]);

  const joinRoom = useCallback(
    async (username: string) => {
      try {
        if (twilioSyncEnabled && syncClient) {
          await joinSyncRoom(roomCode);
        } else {
          await dispatch(
            fetchToken({
              Identity: username,
              Room: roomCode,
            })
          );
        }

        dispatch(removeErrorNotification());
      } catch (err) {
        dispatch(
          setErrorNotification({
            name: err.name,
            type: ErrorType.ConnectionError,
            message: err.message,
          })
        );
        if (
          err.message === "This Video Chat Room is no longer available." ||
          /is already in the room./.test(err.message)
        ) {
          dispatch(setLobbyStep(LobbyStep.EnterName));
        } else {
          dispatch(setLobbyStep(LobbyStep.SelectMediaDevices));
        }

        rollbar.error(err);
      }
    },
    [roomCode, dispatch, syncClient, joinSyncRoom]
  );

  const submitForm = useCallback(() => {
    dispatch(setLobbyStep(LobbyStep.Waiting));
    joinRoom(username);
    //TODO: handle room delegation stuff once implemented server-side
  }, [dispatch, joinRoom, username]);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (lobbyStep === LobbyStep.EnterName) {
        submitRoomCodeAndUserName();
      }

      if (lobbyStep === LobbyStep.RequestPermission) {
        requestPermissions();
      }

      if (lobbyStep === LobbyStep.SelectMediaDevices) {
        submitForm();
      }
    },
    [lobbyStep, requestPermissions, submitForm, submitRoomCodeAndUserName]
  );

  const displayButtonText = useCallback(() => {
    switch (lobbyStep) {
      case LobbyStep.EnterName:
        return "Continue";
      case LobbyStep.RequestPermission:
        return "Request Permissions";
      case LobbyStep.SelectMediaDevices:
        return twilioSyncEnabled ? "Knock" : "Join Room";
      case LobbyStep.Waiting:
        return twilioSyncEnabled ? "Waiting for Host..." : "Loading...";
    }
  }, [lobbyStep]);

  const isButtonDisabled = useCallback(() => {
    if (lobbyStep === LobbyStep.SelectMediaDevices) {
      const isBusy = localTracks.busy.audio || localTracks.busy.video;
      return (
        networkQuality === undefined ||
        (twilioSyncEnabled && syncClient === undefined) ||
        isBusy ||
        (error?.type === ErrorType.MediaError &&
          error?.message !== "" &&
          error?.message !== undefined)
      );
    }

    return lobbyStep === LobbyStep.Waiting;
  }, [error, lobbyStep, localTracks.busy, syncClient, networkQuality]);

  const showBrowserError = useCallback(() => {
    return (
      error &&
      (error.type === ErrorType.IncompatibleUserAgent ||
        error.type === ErrorType.WebViewError)
    );
  }, [error]);

  useEffect(() => {
    const browser = getBrowser();
    const os = getOS();

    // H264 webrtc is broken on iOS 15.1
    if (os.name === "iOS" && os.version.includes("15.1")) {
      dispatch(
        setErrorNotification({
          type: ErrorType.IncompatibleUserAgent,
          name: "iOS 15.1 Bug",
          message: `${browser.name} on ${os.name} ${os.version} is not supported due to an iOS bug. Please use a different device not running ${os.name} ${os.version} until this issue is resolved in iOS 15.2.`,
        })
      );
    } else if (!isSupported) {
      dispatch(
        setErrorNotification({
          type: ErrorType.IncompatibleUserAgent,
          name: "Unsupported Browser",
          message: `${browser.name} ${browser.version} on ${os.name} ${
            os.version
          } is not supported. Please open this web page in a recent version of ${
            os.name === "iOS"
              ? "Safari."
              : os.name === "Android"
              ? "Chrome or Firefox."
              : os.name === "Windows"
              ? "Chrome, Firefox, or Edge."
              : os.name === "macOS"
              ? "Safari, Chrome, Firefox, or Edge."
              : "Chrome or Firefox."
          }`,
        })
      );
    }
  }, [dispatch]);

  useEffect(() => {
    if (isWebView()) {
      dispatch(setErrorNotification(webViewError));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!isSupported) return;
    async function updateSelectedMediaDevices() {
      if (mediaDevices.audioOutputs.length && !localTracks.audioOutput) {
        const defaultDevice = mediaDevices.audioOutputs.find(
          (a) => a.isDefault
        );
        dispatch(
          setAudioOutput({
            deviceId: (defaultDevice || mediaDevices.audioOutputs[0]).deviceId,
            label: (defaultDevice || mediaDevices.audioOutputs[0]).label,
            isDefault: defaultDevice?.isDefault || false
          })
        );
      }
    }

    updateSelectedMediaDevices();
  }, [dispatch, mediaDevices, localTracks.audioOutput]);

  useEffect(() => {
    if (!isSupported) return;
    async function checkDevicePermissions() {
      try {
        const { payload }: PayloadAction<any> = await dispatch(
          updateMediaDevices()
        );
        const { audioInputs, videoInputs, audioOutputs }: MediaDevicesState =
          payload;

        const hasInputPermissions = [
          ...audioInputs,
          ...videoInputs,
          ...audioOutputs,
        ]
          .filter((d) => d.kind === "audioinput" || d.kind === "videoinput")
          .some((d) => d.deviceId !== "" && d.label !== "");

        if (hasInputPermissions) {
          let videoTrack = videoInputs[0];
          let audioTrack = audioInputs[0];

          let options: MediaStreamConstraints = {
            audio: true,
            video: {
              aspectRatio: 1.7777777778,
              width: { ideal: 1280 },
              height: { ideal: 720 },
            },
          };
          const stream = await navigator.mediaDevices.getUserMedia(options);

          if (/Firefox/.test(getBrowser().name || "")) {
            const permittedVideoDeviceId = stream
              .getVideoTracks()[0]
              ?.getSettings()?.deviceId;
            const permittedAudioDeviceId = stream
              .getAudioTracks()[0]
              ?.getSettings()?.deviceId;
            videoTrack = videoInputs.find(
              (v) => v.deviceId === permittedVideoDeviceId
            );
            audioTrack = audioInputs.find(
              (a) => a.deviceId === permittedAudioDeviceId
            );
          }

          dispatch(
            setLocalVideoTrackInfo({
              deviceId: videoTrack?.deviceId || "",
              label: videoTrack?.label || "",
            })
          );

          dispatch(
            setLocalAudioTrackInfo({
              deviceId: audioTrack?.deviceId || "",
              label: audioTrack?.label || "",
              isDefault: audioTrack?.isDefault
            })
          );
        }

        setHasGrantedPermissions(hasInputPermissions);
      } catch (err) {
        dispatch(
          setErrorNotification({
            name: err.name,
            type: ErrorType.MediaError,
            message: err.message,
          })
        );

        rollbar.error(err);
      }
    }

    if (!localTracks.audioTrackInfo && !localTracks.videoTrackInfo) {
      checkDevicePermissions();
    }

  }, [dispatch, localTracks.audioTrackInfo, localTracks.videoTrackInfo]);

  useEffect(() => {
    navigator.mediaDevices.ondevicechange = async function (_) {
      dispatch(updateMediaDevices());
    };
  }, [dispatch]);

  return (
    <>
      <div className={styles["lobby-container"]}>
        <div className={styles["dialog"]}>
          <form onSubmit={onSubmit}>
            <div className={styles["content"]}>
              <div className={`${styles["content-top"]} mb-2`}>
                <div
                  className={`mb-2 ${styles["switcher-image"]} ${
                    isMobile && lobbyStep === LobbyStep.SelectMediaDevices
                      ? styles["hide"]
                      : ""
                  }`}
                ></div>
                <p className="lead text-center mb-0">Remote Guest</p>
              </div>
              <div className={`${styles["content-bottom"]}`}>
                {showBrowserError() ? (
                  <ErrorAlert error={error} />
                ) : (
                  <>
                    {error && error.type === ErrorType.ConnectionError && (
                      <ErrorAlert error={error} />
                    )}
                    {lobbyStep === LobbyStep.EnterName && (
                      <EnterName
                        onRoomCodeChange={(
                          e: React.ChangeEvent<HTMLInputElement>
                        ) => setRoomCode(e.target.value)}
                        onNameChange={(
                          e: React.ChangeEvent<HTMLInputElement>
                        ) => dispatch(setUsername(e.target.value))}
                        name={username}
                        roomCode={roomCode}
                        showRoomCodeInput={!roomUid}
                      />
                    )}
                    {lobbyStep === LobbyStep.RequestPermission && (
                      <RequestPermissions error={error} />
                    )}
                    {lobbyStep === LobbyStep.SelectMediaDevices &&
                      localTracks?.videoTrackInfo?.deviceId &&
                      localTracks?.audioTrackInfo?.deviceId && (
                        <>
                          <SelectMediaDevices
                            showPreview={true}
                            showMicTest={true}
                          />
                        </>
                      )}
                    {lobbyStep === LobbyStep.Waiting && (
                      <div>
                        <p className="lead text-center">
                          {twilioSyncEnabled
                            ? "You're in the Waiting Area."
                            : "Loading..."}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className={styles["footer"]}>
              {!showBrowserError() && (
                <>
                  <button
                    type="submit"
                    className="btn btn-primary btn-block"
                    disabled={isButtonDisabled()}
                  >
                    {displayButtonText()}
                  </button>
                  <a
                    className={styles["switcher-about-link"]}
                    href="https://www.switcherstudio.com/?utm_medium=referral&utm_source=remoteguest&utm_campaign=july2021&utm_content=sslinkentry"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    What is Switcher Studio?
                  </a>
                </>
              )}
            </div>
          </form>
        </div>
        {showNetworkQualityHelp && <NetworkHelpDialog />}
        {showAudioTest && <AudioTestDialog />}
      </div>
    </>
  );
};
